import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Sparkles, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Chip, Stars, Money } from "@/components/ui-veda/Primitives";
import { TID } from "@/constants/testIds";

const CATEGORIES = [
    "All",
    "Immunity",
    "Digestion",
    "Stress Relief",
    "Skin Care",
    "Hair Care",
    "Women Wellness",
    "Body Care",
];

const HERO_IMAGE =
    "https://images.unsplash.com/photo-1726241966580-709b25078be1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxtb3J0YXIlMjBwZXN0bGUlMjBsZWF2ZXN8ZW58MHx8fHwxNzg0MDA1NjY2fDA&ixlib=rb-4.1.0&q=85";

export default function Shop() {
    const [category, setCategory] = useState("All");
    const [q, setQ] = useState("");
    const [products, setProducts] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const ctrl = new AbortController();
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const params = {};
                if (category && category !== "All") params.category = category;
                if (q.trim()) params.q = q.trim();
                const { data } = await api.get("/products", {
                    params,
                    signal: ctrl.signal,
                });
                setProducts(data);
            } catch (e) {
                if (e.name !== "CanceledError") console.error(e);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => {
            ctrl.abort();
            clearTimeout(t);
        };
    }, [category, q]);

    useEffect(() => {
        api.get("/doctors").then((r) => setDoctors(r.data)).catch(() => {});
    }, []);

    const topDoctors = useMemo(
        () =>
            [...doctors]
                .sort((a, b) => (b.rating || 0) - (a.rating || 0))
                .slice(0, 6),
        [doctors],
    );

    return (
        <div data-testid={TID.customerHome}>
            {/* Hero */}
            <section className="border-b border-veda-border">
                <div className="veda-container grid items-center gap-10 py-14 md:grid-cols-[1.15fr_1fr] md:gap-16 md:py-20 lg:py-24">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                            <Sparkles size={12} className="mr-1 inline" />
                            Ayurveda for the everyday
                        </p>
                        <h1 className="font-display mt-4 text-4xl leading-[1.05] text-veda-onSurface sm:text-5xl md:text-6xl lg:text-7xl">
                            Rooted in tradition.
                            <br />
                            <span className="italic text-veda-brand">
                                Simple to live by.
                            </span>
                        </h1>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-veda-muted md:text-lg">
                            Consult certified Vaidyas, order authentic herbal
                            remedies, and stay well — all in one calm place.
                        </p>

                        <div className="mt-8 flex max-w-xl items-center border border-veda-border bg-veda-surface">
                            <Search
                                size={16}
                                strokeWidth={1.75}
                                className="ml-4 text-veda-muted"
                            />
                            <input
                                data-testid={TID.heroSearch}
                                type="search"
                                placeholder="Search remedies (e.g. Ashwagandha, Triphala)"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                className="flex-1 bg-transparent px-3 py-3.5 text-sm text-veda-onSurface placeholder:text-veda-muted focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="relative">
                        <div className="absolute -inset-4 -z-10 bg-veda-brand/10 md:-inset-6" />
                        <img
                            src={HERO_IMAGE}
                            alt="Ayurvedic mortar and herbs"
                            className="h-[320px] w-full object-cover sm:h-[400px] md:h-[520px]"
                            loading="eager"
                        />
                    </div>
                </div>
            </section>

            {/* Categories */}
            <section className="sticky top-16 z-20 border-b border-veda-border bg-veda-surface">
                <div className="veda-container no-scrollbar flex h-14 items-center gap-2 overflow-x-auto">
                    {CATEGORIES.map((c) => (
                        <Chip
                            key={c}
                            active={category === c}
                            onClick={() => setCategory(c)}
                            testid={TID.categoryChip(
                                c.replace(/\s+/g, "-").toLowerCase(),
                            )}
                        >
                            {c}
                        </Chip>
                    ))}
                </div>
            </section>

            {/* Products */}
            <section className="veda-container py-12 md:py-16">
                <div className="mb-6 flex items-baseline justify-between">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                            The catalogue
                        </p>
                        <h2 className="font-display mt-1 text-3xl text-veda-onSurface md:text-4xl">
                            {category === "All" ? "Remedies" : category}
                        </h2>
                    </div>
                    <span className="text-xs text-veda-muted">
                        {products.length} item{products.length !== 1 ? "s" : ""}
                    </span>
                </div>
                {renderProductsBody({ loading, products })}
            </section>

            {/* Top doctors */}
            {topDoctors.length > 0 && (
                <section id="doctors" className="border-t border-veda-border bg-veda-surface2/40">
                    <div className="veda-container py-12 md:py-16">
                        <div className="mb-6 flex items-baseline justify-between">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-muted">
                                    Meet our
                                </p>
                                <h2 className="font-display mt-1 text-3xl text-veda-onSurface md:text-4xl">
                                    Vaidyas &amp; specialists
                                </h2>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                            {topDoctors.map((d) => (
                                <Link
                                    key={d.doctor_id}
                                    to={`/doctor/${d.doctor_id}`}
                                    data-testid={TID.doctorCard(d.doctor_id)}
                                    className="group block border border-veda-border bg-veda-surface transition-colors hover:border-veda-borderStrong"
                                >
                                    <div className="aspect-square w-full overflow-hidden bg-veda-surface2">
                                        <img
                                            src={d.avatar_url}
                                            alt={d.name}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-3">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-brand">
                                            {d.specialty}
                                        </p>
                                        <p className="font-display mt-1 line-clamp-1 text-base text-veda-onSurface">
                                            {d.name}
                                        </p>
                                        <div className="mt-2 flex items-center justify-between text-xs text-veda-muted">
                                            <Stars value={d.rating} size={11} />
                                            <span>
                                                <Money value={d.consultation_fee} />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* CTA banner */}
            <section className="border-t border-veda-border">
                <div className="veda-container py-14 md:py-20">
                    <div className="grid items-center gap-10 border border-veda-border bg-veda-surface p-8 md:grid-cols-[1.1fr_1fr] md:p-14">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                                Personalised care
                            </p>
                            <h2 className="font-display mt-2 text-3xl text-veda-onSurface md:text-4xl">
                                Talk to a Vaidya,
                                <br />
                                <span className="italic text-veda-brand">
                                    from wherever you are.
                                </span>
                            </h2>
                            <p className="mt-4 max-w-md text-sm leading-relaxed text-veda-muted md:text-base">
                                Book a certified Ayurvedic practitioner, chat
                                one-on-one, and receive a bespoke remedy plan —
                                all in one calm place.
                            </p>
                            <a
                                href="#doctors"
                                data-testid={TID.homeCtaBookLink}
                                className="mt-6 inline-flex items-center gap-2 bg-veda-inverse px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-veda-onInverse transition-transform duration-200 hover:-translate-y-0.5"
                            >
                                Meet the Vaidyas{" "}
                                <ArrowUpRight size={14} strokeWidth={1.75} />
                            </a>
                        </div>
                        <div className="hidden md:block">
                            <img
                                src={HERO_IMAGE}
                                alt=""
                                className="h-72 w-full object-cover"
                                loading="lazy"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"];

function renderProductsBody({ loading, products }) {
    if (loading) {
        return (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                {SKELETON_KEYS.map((k) => (
                    <div
                        key={k}
                        className="animate-pulse border border-veda-border bg-veda-surface2"
                    >
                        <div className="aspect-[4/5] w-full bg-veda-divider" />
                        <div className="p-3">
                            <div className="h-3 w-3/4 bg-veda-divider" />
                            <div className="mt-2 h-3 w-1/3 bg-veda-divider" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    if (products.length === 0) {
        return (
            <div className="border border-veda-border bg-veda-surface2 p-10 text-center text-sm text-veda-muted">
                No remedies found.
            </div>
        );
    }
    return (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
            {products.map((p) => (
                <Link
                    key={p.product_id}
                    to={`/product/${p.product_id}`}
                    data-testid={TID.productCard(p.product_id)}
                    className="group block border border-veda-border bg-veda-surface transition-colors hover:border-veda-borderStrong"
                >
                    <div className="aspect-[4/5] w-full overflow-hidden bg-veda-surface2">
                        <img
                            src={p.image_url}
                            alt={p.name}
                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                            loading="lazy"
                        />
                    </div>
                    <div className="p-3">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                            {p.category}
                        </p>
                        <p className="font-display mt-1 line-clamp-2 min-h-[42px] text-[15px] text-veda-onSurface">
                            {p.name}
                        </p>
                        <div className="mt-2 flex items-center justify-between">
                            <p className="text-sm font-semibold text-veda-onSurface">
                                <Money value={p.price} />
                            </p>
                            {p.rating > 0 && <Stars value={p.rating} size={11} />}
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );
}
